import { Component } from '@theme/component';

class StudstashModel extends Component {
  #abort;
  #raf;
  #viewer;
  #loaded = false;
  #loading = false;
  #color = '#576FBD';
  #hue = 226;
  #saturation = .436;
  #lightness = .541;
  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#viewer = this.querySelector('model-viewer');
    this.frameView();
    matchMedia('(max-width: 700px)').addEventListener('change', () => this.frameView(), options);
    const on = (name, event, fn) => this.querySelector(`[data-${name}]`)?.addEventListener(event, fn, options);
    on('load', 'click', () => this.loadPreview());
    on('animation', 'change', event => {
      this.#viewer.animationName = event.target.value;
      this.#viewer.currentTime = 0;
      this.#viewer.play();
      this.updatePlayback();
    });
    on('play', 'click', () => {
      this.#viewer.paused ? this.#viewer.play() : this.#viewer.pause();
      this.updatePlayback();
    });
    on('restart', 'click', () => {
      this.#viewer.currentTime = 0;
      this.#viewer.play();
      this.updatePlayback();
    });
    on('view', 'click', () => {
      this.frameView();
    });
    on('scrub', 'input', event => {
      this.#viewer.pause();
      this.#viewer.currentTime = Number(event.target.value) * this.#viewer.duration;
      this.updatePlayback();
    });
    on('color', 'input', event => this.setColor(event.target.value));
    on('hex', 'change', event => {
      const color = event.target.value.trim();
      if (/^#[\da-f]{6}$/i.test(color)) this.setColor(color);
      else { event.target.value = this.#color; this.status('Enter a six-digit color, such as #576FBD.'); }
    });
    on('hue', 'input', event => {
      this.#hue = Number(event.target.value);
      this.setColor(this.hslHex(this.#lightness), false);
    });
    on('lightness', 'input', event => {
      this.#lightness = Number(event.target.value) / 100;
      this.setColor(this.hslHex(this.#lightness), false);
    });
    on('reset-color', 'click', () => this.setColor('#576FBD'));
    on('variant', 'change', event => {
      const option = event.target.selectedOptions[0];
      this.querySelector('[data-price]').textContent = option.dataset.price;
      const submit = this.querySelector('[data-cart]');
      submit.disabled = option.disabled;
      submit.textContent = option.disabled ? 'Sold out' : 'Add to cart';
      this.querySelector('[data-version-note]').textContent = option.dataset.note || '';
      const url = new URL(location.href);
      url.searchParams.set('variant', option.value);
      history.replaceState(null, '', url);
    });
    this.#viewer.addEventListener('load', () => this.ready(), options);
    this.#viewer.addEventListener('error', () => {
      this.#loading = false;
      this.querySelector('[data-load]').hidden = false;
      this.querySelector('[data-load]').textContent = 'Retry 3D preview';
      this.status('The 3D preview could not load. You can still inspect the product image.');
    }, options);
    this.#viewer.addEventListener('play', () => this.updatePlayback(), options);
    this.#viewer.addEventListener('pause', () => this.updatePlayback(), options);
    let saved;
    try { saved = localStorage.getItem(`studstash-color-${this.dataset.productId}`); } catch {}
    this.setColor(/^#[\da-f]{6}$/i.test(saved || '') ? saved : '#576FBD', true, false);
    this.#raf = requestAnimationFrame(() => this.tick());
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    cancelAnimationFrame(this.#raf);
    this.#viewer?.pause?.();
  }
  frameView() {
    this.#viewer.cameraOrbit = matchMedia('(max-width: 700px)').matches ? '28deg 74deg 115%' : '28deg 74deg 70%';
    this.#viewer.cameraTarget = 'auto auto auto';
    this.#viewer.fieldOfView = '30deg';
    this.#viewer.jumpCameraToGoal?.();
  }
  status(text) { this.querySelector('[data-status]').textContent = text; }
  async loadPreview() {
    if (this.#loading || this.#loaded) return;
    this.#loading = true;
    const button = this.querySelector('[data-load]');
    button.textContent = 'Loading 3D preview…';
    try {
      // The existing model-viewer library is loaded only when requested.
      if (!customElements.get('model-viewer')) await import('https://cdn.jsdelivr.net/npm/@google/model-viewer@4.1.0/dist/model-viewer.min.js');
      await customElements.whenDefined('model-viewer');
      this.#viewer.src = this.#viewer.dataset.src;
      this.#viewer.dismissPoster();
    } catch {
      this.#loading = false;
      button.textContent = 'Retry 3D preview';
      this.status('The 3D preview could not load. Please try again.');
    }
  }
  ready() {
    this.#loaded = true;
    this.#loading = false;
    this.querySelector('[data-load]').hidden = true;
    this.querySelector('[data-poster]').hidden = true;
    const select = this.querySelector('[data-animation]');
    select.replaceChildren();
    for (const name of this.#viewer.availableAnimations) select.add(new Option(name, name));
    for (const control of this.querySelectorAll('[data-animation], [data-play], [data-restart], [data-scrub], [data-view]')) control.disabled = false;
    if (this.#viewer.availableAnimations.includes('Idle')) this.#viewer.animationName = 'Idle';
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) this.#viewer.play();
    this.applyMaterials();
    this.updatePlayback();
    this.status('Drag to rotate. Scroll or pinch to zoom.');
  }
  updatePlayback() {
    if (!this.#loaded) return;
    const button = this.querySelector('[data-play]');
    button.textContent = this.#viewer.paused ? 'Play' : 'Pause';
    button.setAttribute('aria-pressed', String(!this.#viewer.paused));
  }
  setColor(value, updateHue = true, load = true) {
    this.#color = value.toUpperCase();
    for (const control of this.querySelectorAll('[data-color], [data-hex], [data-color-property]')) control.value = this.#color;
    this.querySelector('[data-color-value]').textContent = this.#color;
    this.querySelector('[data-color-chip]').style.background = this.#color;
    if (updateHue) {
      const [r,g,b] = [1,3,5].map(i => parseInt(value.slice(i,i+2),16)/255);
      const max=Math.max(r,g,b), min=Math.min(r,g,b), delta=max-min;
      this.#lightness=(max+min)/2;
      // Keep the chosen hue at white/black so sliding back restores its color.
      if(delta) {
        this.#hue=60*(max===r?((g-b)/delta+6)%6:max===g?(b-r)/delta+2:(r-g)/delta+4);
        this.#saturation=delta/(1-Math.abs(2*this.#lightness-1));
      }
    }
    this.querySelector('[data-hue]').value = String(Math.round(this.#hue));
    this.querySelector('[data-lightness]').value = String(Math.round(this.#lightness*100));
    this.querySelector('[data-lightness-value]').textContent = `${Math.round(this.#lightness*100)}%`;
    this.querySelector('[data-lightness]').style.setProperty('--ss-mid-color', this.hslHex(.5));
    try { localStorage.setItem(`studstash-color-${this.dataset.productId}`, this.#color); } catch {}
    this.applyMaterials();
    if (load && !this.#loaded) this.loadPreview();
  }
  hslHex(lightness) {
    const h=this.#hue/60, c=(1-Math.abs(2*lightness-1))*this.#saturation;
    const x=c*(1-Math.abs(h%2-1)), m=lightness-c/2;
    const rgb=h<1?[c,x,0]:h<2?[x,c,0]:h<3?[0,c,x]:h<4?[0,x,c]:h<5?[x,0,c]:[c,0,x];
    return '#'+rgb.map(v=>Math.round((v+m)*255).toString(16).padStart(2,'0')).join('');
  }
  applyMaterials() {
    if (!this.#loaded || !this.#viewer.model) return;
    const rgb = [1,3,5].map(i => parseInt(this.#color.slice(i,i+2),16)/255).map(c => c<=.04045?c/12.92:((c+.055)/1.055)**2.4);
    for (const material of this.#viewer.model.materials) {
      let factor;
      if (material.name.startsWith('Cobalt blue')) factor=1;
      if (material.name.startsWith('Body blue highlight')) factor=1.35;
      if (material.name.startsWith('Midnight accents')) factor=.36;
      if (factor !== undefined) material.pbrMetallicRoughness.setBaseColorFactor([...rgb.map(c=>Math.min(1,c*factor)),1]);
    }
  }
  tick() {
    if (this.#loaded && this.#viewer.duration > 0) {
      this.querySelector('[data-scrub]').value = String(this.#viewer.currentTime / this.#viewer.duration);
      this.querySelector('[data-time]').textContent = `${this.#viewer.currentTime.toFixed(1)} / ${this.#viewer.duration.toFixed(1)}s`;
    }
    this.#raf=requestAnimationFrame(() => this.tick());
  }
}
if (!customElements.get('studstash-model')) customElements.define('studstash-model', StudstashModel);
